import Dexie, { type Table } from 'dexie';

/**
 * Cache lokal PWA lapangan (T-P5-11, §7.1).
 *
 * Seluruh layar membaca dari sini, bukan dari jaringan: itu yang membuat mode
 * online dan offline tampak identik bagi collector. Jaringan hanya disentuh
 * `syncEngine` dan `mediaQueue`.
 *
 * Field pribadi (NIK, telepon, alamat) disimpan sebagai ciphertext AES-GCM dan
 * hanya bisa dibaca setelah kunci lokal dibuka PIN/biometrik; `searchTokens`
 * adalah HMAC yang membuat pencarian tetap jalan tanpa menyimpan plaintext yang
 * dapat dibaca langsung dari devtools.
 */

/** Ciphertext AES-GCM terserialisasi (lihat `crypto.ts`). */
export type Sealed = string;

export interface CachedCustomer {
  id: string;
  branchId: string;
  cif: string;
  fullName: Sealed;
  nikLast4: string | null;
  birthPlace: string | null;
  birthDate: string | null;
  gender: string | null;
  occupation: string | null;
  phonePrimary: Sealed | null;
  phoneAlt: Sealed | null;
  searchTokens: string[];
  updatedAt: string;
}

export interface CachedLoan {
  id: string;
  customerId: string;
  branchId: string;
  loanNumber: string;
  productName: string | null;
  plafon: string;
  outstanding: string;
  arrearsPrincipal: string;
  arrearsInterest: string;
  penalty: string;
  installmentAmount: string;
  nextDueDate: string | null;
  oldestUnpaidDueDate: string | null;
  dpd: number;
  collectibility: number;
  status: string;
  searchTokens: string[];
  updatedAt: string;
}

export interface CachedAddress {
  id: string;
  customerId: string;
  type: string;
  line1: Sealed;
  rtRw: string | null;
  kelurahan: string | null;
  kecamatan: string | null;
  city: string | null;
  province: string | null;
  postalCode: string | null;
  latitude: number | null;
  longitude: number | null;
  geoSource: string | null;
  geoUpdatedAt: string | null;
  isPrimary: boolean;
  updatedAt: string;
}

export interface CachedContact {
  id: string;
  customerId: string;
  relation: string;
  name: Sealed;
  phone: Sealed | null;
  address: Sealed | null;
}

export interface CachedCollateral {
  id: string;
  loanId: string;
  type: string;
  description: string | null;
  documentNo: string | null;
  appraisedValue: string | null;
  appraisedAt: string | null;
  locationText: string | null;
  latitude: number | null;
  longitude: number | null;
}

export interface CachedPaymentHistory {
  id: string;
  loanId: string;
  externalRef: string;
  paidAt: string;
  amount: string;
  allocPrincipal: string;
  allocInterest: string;
  allocPenalty: string;
  channel: string | null;
}

export interface CachedVisit {
  id: string;
  clientUuid: string;
  loanId: string;
  customerId: string;
  collectorId: string;
  checkinAt: string;
  checkoutAt: string | null;
  result: string;
  resultSubReason: string | null;
  notes: string | null;
  isFlagged: boolean;
  flagReason: string | null;
  distanceToCustomerM: number | null;
  serverReceivedAt: string;
  /** Kunjungan yang masih menunggu kirim ditandai supaya UI dapat membedakannya. */
  pending?: boolean;
}

export type OutboxEntity = 'visit' | 'payment' | 'ptp' | 'address_geo';

/**
 * Status item outbox.
 *
 * `NEEDS_ATTENTION` bukan sekadar "gagal": item itu ditolak server karena isinya
 * dan tidak boleh dicoba ulang otomatis — collector harus memperbaikinya dari
 * layar "Antrean Bermasalah" (§7.2).
 */
export type OutboxStatus = 'PENDING' | 'SENDING' | 'SENT' | 'CONFLICT' | 'NEEDS_ATTENTION';

export interface OutboxItem {
  clientUuid: string;
  entity: OutboxEntity;
  op: 'create' | 'update';
  payload: Record<string, unknown>;
  status: OutboxStatus;
  attempts: number;
  /** Waktu paling awal item boleh dikirim ulang — dasar backoff eksponensial. */
  nextAttemptAt: string;
  createdAt: string;
  lastError: string | null;
  serverId: string | null;
  conflictId: string | null;
}

export type MediaQueueStatus = 'PENDING' | 'UPLOADING' | 'UPLOADED' | 'FAILED';

export interface MediaQueueItem {
  clientUuid: string;
  /** `clientUuid` kunjungan/pembayaran pemilik berkas; penautan terjadi di server. */
  ownerClientUuid: string | null;
  kind: 'VISIT_PHOTO' | 'SIGNATURE' | 'PAYMENT_PROOF';
  mimeType: string;
  blob: Blob;
  sizeBytes: number;
  sha256: string;
  capturedAt: string | null;
  latitude: number | null;
  longitude: number | null;
  status: MediaQueueStatus;
  /** Diisi server saat `init`; dipakai melanjutkan unggahan yang terputus. */
  mediaId: string | null;
  uploadedBytes: number;
  attempts: number;
  nextAttemptAt: string;
  createdAt: string;
  lastError: string | null;
}

export interface MetaRecord {
  key: string;
  value: unknown;
  updatedAt: string;
}

export class FieldDB extends Dexie {
  loans!: Table<CachedLoan, string>;
  customers!: Table<CachedCustomer, string>;
  addresses!: Table<CachedAddress, string>;
  contacts!: Table<CachedContact, string>;
  collaterals!: Table<CachedCollateral, string>;
  paymentHistory!: Table<CachedPaymentHistory, string>;
  visitHistory!: Table<CachedVisit, string>;
  outbox!: Table<OutboxItem, string>;
  mediaQueue!: Table<MediaQueueItem, string>;
  meta!: Table<MetaRecord, string>;

  constructor(name = 'bssb-field') {
    super(name);

    this.version(1).stores({
      loans: 'id, customerId, collectibility, dpd, status, *searchTokens',
      customers: 'id, cif, *searchTokens',
      addresses: 'id, customerId',
      contacts: 'id, customerId',
      collaterals: 'id, loanId',
      paymentHistory: 'id, loanId, paidAt',
      visitHistory: 'id, loanId, checkinAt',
      outbox: 'clientUuid, entity, status, createdAt, attempts, nextAttemptAt',
      mediaQueue: 'clientUuid, status, ownerClientUuid, createdAt, nextAttemptAt',
      meta: 'key',
    });
  }
}

export const db = new FieldDB();

/** Kunci `meta` yang dipakai lintas modul — hindari string lepas di banyak berkas. */
export const META_KEYS = {
  /** Cursor `sync/pull` per entitas: `{ [entity]: cursor }`. */
  cursors: 'sync.cursors',
  schemaVersion: 'sync.schemaVersion',
  lastSyncAt: 'sync.lastSyncAt',
  parameters: 'app.parameters',
  limits: 'app.limits',
  /** Data key terenkripsi + parameter pembungkusnya (lihat `crypto.ts`). */
  wrappedKey: 'crypto.wrappedKey',
  searchSalt: 'crypto.searchSalt',
  session: 'auth.session',
} as const;

export async function readMeta<T>(key: string): Promise<T | undefined> {
  const row = await db.meta.get(key);
  return row?.value as T | undefined;
}

export async function writeMeta(key: string, value: unknown): Promise<void> {
  await db.meta.put({ key, value, updatedAt: new Date().toISOString() });
}

export async function deleteMeta(key: string): Promise<void> {
  await db.meta.delete(key);
}

/**
 * Hapus seluruh cache lokal (FR-AUTH-10, FR-SYNC-11).
 *
 * Outbox ikut dihapus: perintah wipe datang dari perangkat yang dianggap hilang
 * atau kedaluwarsa, dan menyimpan sisa transaksi lapangan di sana justru
 * menyisakan data nasabah yang seharusnya lenyap.
 */
export async function wipeLocalData(): Promise<void> {
  await db.transaction(
    'rw',
    [
      db.loans,
      db.customers,
      db.addresses,
      db.contacts,
      db.collaterals,
      db.paymentHistory,
      db.visitHistory,
      db.outbox,
      db.mediaQueue,
      db.meta,
    ],
    async () => {
      await Promise.all([
        db.loans.clear(),
        db.customers.clear(),
        db.addresses.clear(),
        db.contacts.clear(),
        db.collaterals.clear(),
        db.paymentHistory.clear(),
        db.visitHistory.clear(),
        db.outbox.clear(),
        db.mediaQueue.clear(),
        db.meta.clear(),
      ]);
    },
  );
}
